import { Button, Modal, RefreshCw } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useFacilityUser from "../../../hooks/useFacilityUser";
import { AppointmentStatusEnum } from "../../../types/appointment.definition";
import { Doctor, MIN_BOOKING_HOURS_AHEAD, ProvidedServiceEnum } from "../../../types/doctor.definition";
import { PublicPatient } from "../../../types/patient.definition";
import {
  DoctorAvailabilitySelector,
  DoctorAvailabilitySelectorRef,
} from "../DoctorAvailabilitySelector/DoctorAvailabilitySelector";
import { DoctorSearchAutocomplete } from "../DoctorSearchAutocomplete/DoctorSearchAutocomplete";
import { PatientSearchAutocomplete } from "../PatientSearchAutocomplete/PatientSearchAutocomplete";

// medecine360-api refuses appointments less than 4 hours ahead
// (max-appointment-hours-ahead): don't offer those slots.

interface AppointmentFormProps {
  onSubmit: (appointmentData: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  facilityId: number;
  initialPatientId?: string;
}

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  facilityId,
  initialPatientId,
}) => {
  const { t } = useTranslation();
  const facilityUser = useFacilityUser();
  const [formData, setFormData] = useState({
    patientId: initialPatientId || "",
    doctorId: "",
    appointmentTime: "",
    appointmentType: ProvidedServiceEnum.CLINIC,
    status: AppointmentStatusEnum.CONFIRMED,
    reason: "",
    timeSlotId: "",
  });

  // Update patientId when initialPatientId changes
  useEffect(() => {
    if (initialPatientId) {
      setFormData((prev) => ({ ...prev, patientId: initialPatientId }));
    }
  }, [initialPatientId]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | undefined>(undefined);
  const [selectedPatient, setSelectedPatient] = useState<PublicPatient | undefined>(undefined);

  // Ref for doctor availability selector to access refresh function
  const availabilitySelectorRef = useRef<DoctorAvailabilitySelectorRef>(null);

  // Use useCallback for handlers to prevent unnecessary re-renders
  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error for this field
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const handlePatientSelect = useCallback((patientId: string, patient?: PublicPatient) => {
    setFormData((prev) => ({ ...prev, patientId }));
    setSelectedPatient(patient);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.patientId;
      return newErrors;
    });
  }, []);

  const handleDoctorSelect = useCallback(
    (doctorId: string, doctor?: Doctor) => {
      // Only update if the doctor ID actually changed
      setFormData((prev) => {
        if (prev.doctorId === doctorId) return prev;
        return {
          ...prev,
          doctorId,
          timeSlotId: "", // Reset slot when doctor changes
          appointmentTime: "", // Reset appointment time when doctor changes
        };
      });

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.doctorId;
        delete newErrors.timeSlotId;
        return newErrors;
      });

      // Store the selected doctor data
      setSelectedDoctor(doctor || undefined);
    },
    []
  );

  const handleSlotSelect = useCallback((slotId: number, startTime: string) => {
    setFormData((prev) => ({
      ...prev,
      timeSlotId: slotId.toString(),
      appointmentTime: startTime,
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.timeSlotId;
      return newErrors;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const newErrors: Record<string, string> = {};

      // Validate required fields
      if (!formData.patientId) {
        newErrors.patientId = t("appointment.form.validation.patientRequired");
      }
      if (!formData.doctorId) {
        newErrors.doctorId = t("appointment.form.validation.doctorRequired");
      }
      if (!formData.timeSlotId) {
        newErrors.timeSlotId = t("appointment.form.validation.timeRequired");
      }
      if (!formData.reason.trim()) {
        newErrors.reason = t("appointment.form.validation.reasonRequired");
      }

      // Validate appointmentTime
      if (!formData.appointmentTime && !newErrors.timeSlotId) {
        newErrors.timeSlotId = t("appointment.form.validation.timeRequired");
      }

      // Validate that appointmentTime is a valid date string if provided
      if (formData.appointmentTime) {
        const appointmentDate = new Date(formData.appointmentTime);
        if (isNaN(appointmentDate.getTime())) {
          newErrors.timeSlotId = t("appointment.form.validation.invalidTime");
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Format the data for API submission to match the expected payload
      // appointmentTime is already in ISO string format from slot selection
      const facilityLocation = facilityUser.healthcareFacility?.location;
      const appointmentData = {
        healthcareFacilityId: facilityId,
        patientId: parseInt(formData.patientId),
        doctorId: parseInt(formData.doctorId),
        timeSlotId: parseInt(formData.timeSlotId),
        appointmentTime: formData.appointmentTime, // Already in ISO format
        appointmentType: formData.appointmentType,
        status: formData.status,
        reason: formData.reason.trim(),
        location: {
          country: facilityLocation?.country,
          address: facilityLocation?.address,
          city: facilityLocation?.city,
          state: facilityLocation?.state,
          zip: facilityLocation?.zip,
        },
      };

      await onSubmit(appointmentData);
    },
    [formData, facilityId, facilityUser, onSubmit, t]
  );

  // Use useMemo for derived values
  const isPatientDisabled = !!selectedPatient?.disabled;
  const isDoctorDisabled = !!selectedDoctor?.disabled;

  const isSubmitDisabled = useMemo(
    () =>
      // MED-1687: missing fields no longer disable the button — clicking it is what shows the
      // message under each one (handleSubmit); the patient and doctor fields had none.
      isSubmitting ||
      isPatientDisabled ||
      isDoctorDisabled,
    [
      isSubmitting,
      isPatientDisabled,
      isDoctorDisabled,
    ]
  );

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={t("appointment.form.title")}
      variant="info"
      size="5xl"
      contentClassName="max-h-[75vh] overflow-y-auto"
    >
      <p className="mb-6 -mt-2 text-sm text-neutral-600">
        {t("appointment.form.subtitle")}
      </p>
      <form onSubmit={handleSubmit} autoComplete="off">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Patient Selection with Autocomplete */}
                <div>
                  <label
                    htmlFor="patientSearch"
                    className="block mb-2 text-sm font-medium text-neutral-700"
                  >
                    {t("appointment.form.patient")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <PatientSearchAutocomplete
                    onSelect={handlePatientSelect}
                    selectedPatientId={formData.patientId}
                    disabled={isSubmitting}
                  />
                  {errors.patientId && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.patientId}
                    </p>
                  )}
                  {isPatientDisabled && (
                    <p className="mt-1 text-xs text-amber-600">
                      {t("appointment.form.patientDisabledWarning")}
                    </p>
                  )}
                </div>

                {/* Doctor Selection with Autocomplete */}
                <div>
                  <label
                    htmlFor="doctorSearch"
                    className="block mb-2 text-sm font-medium text-neutral-700"
                  >
                    {t("appointment.form.doctor")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <DoctorSearchAutocomplete
                    onSelect={handleDoctorSelect}
                    selectedDoctorId={formData.doctorId}
                    disabled={isSubmitting}
                  />
                  {errors.doctorId && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.doctorId}
                    </p>
                  )}
                  {isDoctorDisabled && (
                    <p className="mt-1 text-xs text-amber-600">
                      {t("appointment.form.doctorDisabledWarning")}
                    </p>
                  )}
                </div>
              </div>

              {/* Doctor Availability Selector - Only show when a doctor is selected */}
              {formData.doctorId && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-neutral-700">
                      {t("appointment.form.selectTime")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => availabilitySelectorRef.current?.refresh()}
                      disabled={isSubmitting}
                      className="text-sm"
                    >
                      <RefreshCw className="mr-1 w-4 h-4" />
                      {t("appointment.form.refresh")}
                    </Button>
                  </div>
                  <DoctorAvailabilitySelector
                    ref={availabilitySelectorRef}
                    doctorId={formData.doctorId}
                    doctor={selectedDoctor}
                    onSelectSlot={handleSlotSelect}
                    selectedSlotId={
                      formData.timeSlotId ? parseInt(formData.timeSlotId) : null
                    }
                    disabled={isSubmitting}
                    ownFacilityId={facilityId}
                    minBookingHoursAhead={MIN_BOOKING_HOURS_AHEAD}
                    hideOtherFacilitySlots
                  />
                  {errors.timeSlotId ? (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.timeSlotId}
                    </p>
                  ) : (
                    !formData.timeSlotId && (
                      <p className="mt-1 text-sm text-neutral-500">
                        {t("appointment.form.selectSlot")}
                      </p>
                    )
                  )}
                </div>
              )}

              {/* Reason */}
              <div className="mt-6">
                <label
                  htmlFor="reason"
                  className="block mb-2 text-sm font-medium text-neutral-700"
                >
                  {t("appointment.form.reason")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  value={formData.reason}
                  onChange={handleChange}
                  className={`block px-3 py-2 w-full rounded-md border shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm ${errors.reason ? "border-red-500" : "border-neutral-300"
                    }`}
                  placeholder={t("appointment.form.reasonPlaceholder")}
                  disabled={isSubmitting}
                />
                {errors.reason && (
                  <p className="mt-1 text-xs text-red-500">{errors.reason}</p>
                )}
              </div>

              <div className="flex justify-end mt-8 space-x-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitDisabled}
                  loading={isSubmitting}
                >
                  {isSubmitting
                    ? t("appointment.form.creating")
                    : t("appointment.form.create")}
                </Button>
              </div>
            </form>

            {/* Footer note */}
            <div className="flex justify-between items-center pt-4 mt-6 text-sm border-t text-neutral-600 border-neutral-200">
              <span>{t("appointment.form.requiredFields")}</span>
              <span>{t("appointment.form.infoNote")}</span>
            </div>
    </Modal>
  );
};
